/**
 * Extract mustache variables from text
 * @param {string} text - Text containing mustache variables
 * @returns {Array} - Array of found variables
 */
export default function extractMustacheVariables(text) {
  const variables = [];

  // First, handle mustache function blocks like {{#f_title}}...{{/f_title}}
  // These should be treated as a single unit where the entire block gets replaced
  // Updated regex to match any content between the tags, including multiple variables
  const blockRegex = /{{#(\w+)}}(.*?){{\/\1}}/g;
  let blockMatch;
  const handledPositions = new Set();

  while ((blockMatch = blockRegex.exec(text)) !== null) {
    const fullBlock = blockMatch[0];
    const functionName = blockMatch[1]; // e.g., "f_title"
    const innerContent = blockMatch[2]; // Everything between the tags
    const startIdx = blockMatch.index;
    const endIdx = blockMatch.index + fullBlock.length - 1;

    // Extract all variables from the inner content
    const innerVarRegex = /{{([\w.]+)}}/g;
    const innerVariables = [];
    let innerMatch;

    while ((innerMatch = innerVarRegex.exec(innerContent)) !== null) {
      innerVariables.push({
        variable: innerMatch[1],
        relativeStart: innerMatch.index,
        relativeEnd: innerMatch.index + innerMatch[0].length - 1,
      });
    }

    if (innerVariables.length > 0) {
      // Create a combined variable name for multiple variables
      const variableNames = innerVariables.map((v) => v.variable);
      const combinedVariable = variableNames.join(' ');

      // Calculate the range of the content that contains the variables
      const contentStartInBlock = blockMatch[0].indexOf(innerContent);
      const innerStartIdx =
        startIdx + contentStartInBlock + innerVariables[0].relativeStart;
      const lastVar = innerVariables[innerVariables.length - 1];
      const innerEndIdx = startIdx + contentStartInBlock + lastVar.relativeEnd;

      variables.push({
        fullMatch: fullBlock,
        variable: combinedVariable, // e.g., "s.nome s.cognome"
        variables: variableNames, // Array of individual variable names
        startIndex: startIdx,
        endIndex: endIdx,
        innerStartIndex: innerStartIdx,
        innerEndIndex: innerEndIdx,
        functionName: functionName,
        isWrapped: true,
      });

      // Mark these positions as handled so we don't duplicate them
      for (let i = startIdx; i <= endIdx; i++) {
        handledPositions.add(i);
      }
    }
  }

  // Now handle regular mustache variables that aren't inside function blocks
  const regex = /{{([\w.]+)}}/g;
  let match;

  while ((match = regex.exec(text)) !== null) {
    const startIdx = match.index;
    const endIdx = match.index + match[0].length - 1;

    // Skip if this variable is part of a function block we already handled
    let isInBlock = false;
    for (let i = startIdx; i <= endIdx; i++) {
      if (handledPositions.has(i)) {
        isInBlock = true;
        break;
      }
    }

    if (!isInBlock) {
      variables.push({
        fullMatch: match[0],
        variable: match[1],
        startIndex: startIdx,
        endIndex: endIdx,
      });
    }
  }

  // Sort by startIndex to maintain order
  variables.sort((a, b) => a.startIndex - b.startIndex);

  return variables;
}
